import { Alert, Box, Stack } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTextField, PrimaryButton } from "../../../components/form";
import { userResetPassword } from "../../../api/auth";
import { useMutationWithDispatch } from "../../../hooks/useMutationWithDispatch";
import {
  resetPasswordSchema,
  ResetPasswordFormData,
  PAGE_CONFIG,
} from "./config";
import {
  handleResetPasswordSuccess,
  getResetPasswordPayload,
  validateToken,
  TOKEN_ERROR_MESSAGE,
} from "./utils";
import { ResetPasswordResponse, OutletContext } from "./types";
import { SomethingWentWrong } from "../../../components/form/errors";

function ResetPassword() {
  const { setPageConfig } = useOutletContext<OutletContext>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const isValidToken = useMemo(() => validateToken(token), [token]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const { mutateAsync: submitResetPassword, isPending: submitting } =
    useMutationWithDispatch<ResetPasswordResponse, { password: string }>(
      (payload) => userResetPassword(token || "", payload)
    );

  useEffect(() => {
    setPageConfig({ title: PAGE_CONFIG.TITLE });
  }, [setPageConfig]);

  const onSubmit = useCallback(
    async (data: ResetPasswordFormData) => {
      if (!isValidToken) {
        return;
      }

      try {
        const payload = getResetPasswordPayload(data);
        const res = await submitResetPassword(payload);

        if (res?.success) {
          handleResetPasswordSuccess(navigate);
        }
      } catch (error) {
      }
    },
    [submitResetPassword, navigate, isValidToken]
  );

  if (!isValidToken) {
    return (
      <Box width="100%" mt={5} mb={2} display="flex" justifyContent="center">
        <SomethingWentWrong message={TOKEN_ERROR_MESSAGE} />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap={2}>
        <FormTextField
          name="password"
          control={control}
          label="New Password"
          required
          error={errors.password?.message}
          textFieldProps={{ type: "password" }}
        />

        <FormTextField
          name="confirmPassword"
          control={control}
          label="Repeat Password"
          required
          error={errors.confirmPassword?.message}
          textFieldProps={{ type: "password" }}
        />

        <PrimaryButton loading={submitting}>Reset Password</PrimaryButton>
      </Stack>
    </form>
  );
}

export default ResetPassword;
